import { FieldWrapper, IFieldProps, IWidgetProps, register } from "decorator-form";
import React from "react";

import { BreakpointsField } from './fields/BreakpointsField';
import DefaultConditionField from "./fields/condition/DefaultConditionField";
import JsonField from "./fields/JsonField";
import { KeyValueListField } from "./fields/KeyValueListField";
import { LabelField } from "./fields/label/LabelField";
import { ILayout, LayoutField } from "./fields/layoutField/LayoutField";
import PageField from "./fields/PageField";
import PagePathField from "./fields/PagePathField";
import PagesField from "./fields/PagesField";
import PanelsField from "./fields/PanelsField";
import { UploadField } from "./fields/upload/UploadField";
import { UploadMultiField } from "./fields/upload/UploadMultiField";
import { UploadUrlField } from "./fields/upload/UploadUrlField";
import AlignmentWidget from "./widgets/AlignmentWidget";
import BackgroundImageWidget from "./widgets/BackgroundImageWidget";
import BorderWidget from "./widgets/BorderWidget";
import BoxShadowWidget from "./widgets/BoxShadowWidget";
import CheckBoxWidget from "./widgets/CheckBoxWidget";
import ColorPickerWidget from "./widgets/ColorPickerWidget";
import CssJustifyWidget from "./widgets/CssJustifyWidget";
import { CssTransitionWidget } from "./widgets/CssTransitionWidget";
import CssUnitWidget from "./widgets/CssUnitWidget";
import { DatePickerWidget } from "./widgets/date/DatePickerWidget";
import DirectionWidget from "./widgets/DirectionWidget";
import FontWeightWidget from "./widgets/FontWeightWidget";
import NumberWidget from "./widgets/NumberWidget";
import ObjectFitWidget from './widgets/ObjectFitWidget';
import ObjectPositionWidget from './widgets/ObjectPositionWidget';
import RemoteConditionWidget from "./widgets/RemoteConditionWidget";
import RichTextWidgdet from "./widgets/richText/RichTextWidget";
import SpaceWidget from "./widgets/SpaceWidget";
import { TextAreaWidget } from "./widgets/TextAreaWidget";
import TextTransformWidget from './widgets/TextTransformWidget';
import TextWidget from "./widgets/TextWidget";

/**
 * @overview: Types available for use in the {@link @Input} decorator
 * on config classes. A string enum is used so type can be selected
 * dynamically if needed. Custom widgets registered here should accept
 * {@link IWidgetProps} and fields {@link IFieldProps}
 *
 * @author: jesposito
 */

const InputType = {
	TEXT: "text",
	TEXT_HTML: "texthtml",
	NUMBER: "number",
	NUMBER_POSITIVE: "numberpositive",
	PASSWORD: "password",
	CHECKBOX: "checkbox",
	DATE: "date",
	TEXTAREA: "textarea",
	COLOR: "colorpicker",
	FONT_WEIGHT: "fontweight",
	BORDER: "border",
	BORDER_TOP: "border-top",
	BORDER_RIGHT: "border-right",
	BORDER_BOTTOM: "border-bottom",
	BORDER_LEFT: "border-left",
	MARGIN: "margin",
	PADDING: "padding",
	UNIT: "unit",
	ALIGNMENT: "alignment",
	JUSTIFY: "justify",
	OBJECT_FIT: "objectfit",
	OBJECT_POSITION: "objectposition",
	CSS_TRANSITION: "csstransition",
	TEXT_TRANSFORM: "texttransform",
	BOX_SHADOW: "boxshadow",
	BACKGROUND_IMAGE: "backgroundimage",
	DIRECTION: "direction",
	BREAKPOINTS: "breakpoints", // array of objects
	UNIT_BREAKPOINTS: "unitbreakpoints", // array
	REFINE_TAG_OPTIONS: "refinetagoptions", // configure how many tags a user can select at once
	PANELS: "panel", // array of panel uuids
	VIEW: "view", // view string
	LAYOUT: "layout",
	PAGES: "pages",
	PAGE: "page",
	PAGE_PATH: "pagepath",
	ROLES: "roles",
	GROUPS: "groups",
	PANEL_LAYOUT: "panellayout",
	JSON: "json",

	/**
	 * list of string key/value pairs
	 */
	KEY_VALUE_LIST: "keyvaluelist",
	KEY_VALUE_LIST_FLAT: "keyvaluelistflat",

	// for just adding some text i gues
	NULL: "null",

	// conditions builder for front end
	// use with ConditionTypeRegistry.evaluate(condition)
	// such cache, maybe fast, who knows?
	CONDITION: "condition",
	CONDITION_REMOTE: "conditionremote",

	UPLOAD: "upload",
	UPLOAD_URL: "uploadurl",
	UPLOAD_URL_IMAGE: "uploadurlimg",
	UPLOAD_MULTI: "uploadmulti",

	LABEL: "label"
};

export default InputType;

register(InputType.TEXT, new FieldWrapper({
	dataType: "string", uiWidget: (widgetProps: IWidgetProps<string>) =>
		React.createElement(TextWidget, widgetProps)
}));

register(InputType.TEXTAREA, new FieldWrapper({
	dataType: "string", uiWidget: TextAreaWidget
}));

register(InputType.DATE, new FieldWrapper({
	dataType: "string", uiWidget: DatePickerWidget
}));

register(InputType.TEXT_HTML, new FieldWrapper({
	dataType: "string", uiWidget: RichTextWidgdet
}));

register(InputType.COLOR, new FieldWrapper({
	dataType: "string", uiWidget: ColorPickerWidget
}));

register(InputType.FONT_WEIGHT, new FieldWrapper({
	dataType: "string", uiWidget: FontWeightWidget
}));

register(InputType.BORDER, new FieldWrapper({
	dataType: "string", uiWidget: BorderWidget
}));

register(InputType.BORDER_TOP, new FieldWrapper({
	dataType: "string", uiWidget: BorderWidget
}));

register(InputType.BORDER_RIGHT, new FieldWrapper({
	dataType: "string", uiWidget: BorderWidget
}));

register(InputType.BORDER_BOTTOM, new FieldWrapper({
	dataType: "string", uiWidget: BorderWidget
}));

register(InputType.BORDER_LEFT, new FieldWrapper({
	dataType: "string", uiWidget: BorderWidget
}));

register(InputType.MARGIN, new FieldWrapper({
	dataType: "string", uiWidget: SpaceWidget
}));

register(InputType.PADDING, new FieldWrapper({
	dataType: "string", uiWidget: SpaceWidget
}));

register(InputType.UNIT, new FieldWrapper({
	dataType: "string", uiWidget: CssUnitWidget
}));

register(InputType.ALIGNMENT, new FieldWrapper({
	dataType: "string", uiWidget: AlignmentWidget
}));

register(InputType.JUSTIFY, new FieldWrapper({
	dataType: "string", uiWidget: CssJustifyWidget
}));

register(InputType.NUMBER, new FieldWrapper({
	dataType: "integer", uiWidget: NumberWidget
}));

register(InputType.NUMBER_POSITIVE, new FieldWrapper({
	dataType: "integer", uiWidget: NumberWidget.Positive
}));

register(InputType.CHECKBOX, new FieldWrapper({
	dataType: "boolean", uiWidget: CheckBoxWidget
}));

register(InputType.OBJECT_FIT, new FieldWrapper({
	dataType: "string", uiWidget: ObjectFitWidget
}));

register(InputType.OBJECT_POSITION, new FieldWrapper({
	dataType: "string", uiWidget: ObjectPositionWidget
}));

register(InputType.TEXT_TRANSFORM, new FieldWrapper({
	dataType: "string", uiWidget: TextTransformWidget
}));

register(InputType.CSS_TRANSITION, new FieldWrapper({
	dataType: "string", uiWidget: CssTransitionWidget
}));

register(InputType.BOX_SHADOW, new FieldWrapper({
	dataType: "string", uiWidget: BoxShadowWidget
}));

register(InputType.BACKGROUND_IMAGE, new FieldWrapper({
	dataType: "string", uiWidget: BackgroundImageWidget
}));

register(InputType.DIRECTION, new FieldWrapper({
	dataType: "string", uiWidget: DirectionWidget
}));

register(InputType.BREAKPOINTS, new FieldWrapper({
	dataType: "array", arrayType: "object", uiField: BreakpointsField
}));

register(InputType.PANELS, new FieldWrapper({
	dataType: "array", arrayType: "string", uiField: PanelsField
}));

register(InputType.LAYOUT, new FieldWrapper({
	dataType: "object", uiField: LayoutField
}));

register(InputType.PAGES, new FieldWrapper({
	dataType: "array", uiField: (fieldProps: IFieldProps<ILayout>) =>
		React.createElement(PagesField, fieldProps)
}));

register(InputType.PAGE, new FieldWrapper({
	dataType: "object", uiField: (fieldProps: IFieldProps<ILayout>) =>
		React.createElement(PageField, fieldProps)
}));

register(InputType.PAGE_PATH, new FieldWrapper({
	dataType: "array", arrayType: "string", uiField: (widgetProps: IFieldProps<string[]>) =>
		React.createElement(PagePathField, widgetProps)
}));

register(InputType.ROLES, new FieldWrapper({
	dataType: "array", arrayType: "string"
}));

register(InputType.GROUPS, new FieldWrapper({
	dataType: "array", arrayType: "string"
}));

register(InputType.JSON, new FieldWrapper({
	dataType: "object", uiField: JsonField
}));

register(InputType.KEY_VALUE_LIST, new FieldWrapper({
	dataType: "array", arrayType: "object", uiField: KeyValueListField
}));

register(InputType.KEY_VALUE_LIST_FLAT, new FieldWrapper({
	dataType: "array", arrayType: "object", uiField: (props: IFieldProps<any>) => <KeyValueListField {...props} allowArrays={false} />
}));

register(InputType.NULL, new FieldWrapper({dataType: "null"}));

register(InputType.CONDITION, new FieldWrapper({
	dataType: "object", uiField: DefaultConditionField
}));

register(InputType.UPLOAD, new FieldWrapper({
	dataType: "object", uiField: UploadField
}));

register(InputType.UPLOAD_MULTI, new FieldWrapper({
	dataType: "array", arrayType: "object", uiField: UploadMultiField
}));

register(InputType.UPLOAD_URL, new FieldWrapper({
	dataType: "string", uiField: UploadUrlField
}));

register(InputType.UPLOAD_URL_IMAGE, new FieldWrapper({
	dataType: "string", uiField: UploadUrlField.withOptions({
		allowTypes: ["image/*"],
		type: "image/*",
		urlEditable: true,
	})
}));

register(InputType.LABEL, new FieldWrapper({
	dataType: "object", uiField: LabelField
}));

register(InputType.CONDITION_REMOTE, new FieldWrapper({
	dataType: 'string', uiWidget: RemoteConditionWidget
}));



// WEBPACK FOOTER //
// ./src/common/form/InputType.tsx